import { env } from '@/env';

const PUBLIC_MARKETING_PATHS = new Set([
  '/',
  '/contact',
  '/privacy',
  '/terms',
  '/sms-consent',
  '/opt-in-documentation',
]);

export const isMarketingOnlyMode = env.VITE_MARKETING_ONLY_MODE;

function normalizePathname(pathname: string): string {
  if (pathname === '/') return pathname;
  return pathname.replace(/\/+$/, '');
}

export function isMarketingOnlyPublicPath(pathname: string): boolean {
  return PUBLIC_MARKETING_PATHS.has(normalizePathname(pathname));
}

export function marketingOnlyRedirect(pathname: string): '/' | null {
  return isMarketingOnlyMode && !isMarketingOnlyPublicPath(pathname) ? '/' : null;
}

interface PushSubscriptionLike {
  unsubscribe(): Promise<boolean>;
}

interface ServiceWorkerRegistrationLike {
  unregister(): Promise<boolean>;
  pushManager?: {
    getSubscription(): Promise<PushSubscriptionLike | null>;
  };
}

interface ShutdownClientDependencies {
  localStorage?: Pick<Storage, 'key' | 'length' | 'removeItem'>;
  sessionStorage?: Pick<Storage, 'key' | 'length' | 'removeItem'>;
  cacheStorage?: Pick<CacheStorage, 'keys' | 'delete'>;
  serviceWorker?: {
    getRegistrations(): Promise<readonly ServiceWorkerRegistrationLike[]>;
  };
}

function removeApplicationStorage(storage: ShutdownClientDependencies['localStorage']): void {
  if (!storage) return;

  const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter(
    (key): key is string => key !== null,
  );

  for (const key of keys) {
    if (
      key === 'pirouette.auth' ||
      key === 'pirouette.operator.auth' ||
      /^sb-.+-auth-token$/.test(key)
    ) {
      storage.removeItem(key);
    }
  }
}

function browserDependencies(): ShutdownClientDependencies {
  if (typeof window === 'undefined') return {};

  return {
    localStorage: window.localStorage,
    sessionStorage: window.sessionStorage,
    ...('caches' in window ? { cacheStorage: window.caches } : {}),
    ...('serviceWorker' in window.navigator
      ? { serviceWorker: window.navigator.serviceWorker }
      : {}),
  };
}

/**
 * Remove installed-app state after a marketing-only release. This is best-effort:
 * public marketing access must remain available even if an older browser denies
 * one cleanup API.
 */
export async function deactivateApplicationClient(
  dependencies: ShutdownClientDependencies = browserDependencies(),
): Promise<void> {
  removeApplicationStorage(dependencies.localStorage);
  removeApplicationStorage(dependencies.sessionStorage);

  const cleanupTasks: Promise<unknown>[] = [];

  if (dependencies.cacheStorage) {
    const cacheStorage = dependencies.cacheStorage;
    cleanupTasks.push(
      cacheStorage.keys().then((keys) => Promise.all(keys.map((key) => cacheStorage.delete(key)))),
    );
  }

  if (dependencies.serviceWorker) {
    cleanupTasks.push(
      dependencies.serviceWorker.getRegistrations().then(async (registrations) => {
        await Promise.all(
          registrations.map(async (registration) => {
            const subscription = await registration.pushManager?.getSubscription();
            await subscription?.unsubscribe();
            await registration.unregister();
          }),
        );
      }),
    );
  }

  const results = await Promise.allSettled(cleanupTasks);
  for (const result of results) {
    if (result.status === 'rejected') {
      // Cleanup failure must not take down the public marketing site.
      // eslint-disable-next-line no-console
      console.warn(
        '[marketing-only] Could not clear all installed application state.',
        result.reason,
      );
    }
  }
}
